import React, { useState, useEffect, useMemo } from 'react';

import Button from '../shared/Button';
import { X, LayoutTemplate } from 'lucide-react';
import { getDefaultExportConfig, normalizeHeaderSlot, HEADER_SLOT } from '../../utils/pdfExportUtils';

const OPTIONS = [
	{ label: 'None', value: HEADER_SLOT.NONE },
	{ label: 'Page Number', value: HEADER_SLOT.PAGE_NUMBER },
	{ label: 'Running head', value: HEADER_SLOT.RUNNING_HEAD },
	{ label: 'Document title', value: HEADER_SLOT.DOCUMENT_TITLE },
];

const PositionSelector = ({ label, value, onChange }) => (
	<div className="flex flex-col gap-1 w-full">
		<label className="text-xs font-semibold text-gray-500 uppercase">{label}</label>
		<select
			value={normalizeHeaderSlot(value)}
			onChange={(e) => onChange(e.target.value)}
			className="w-full p-2 border border-gray-200 rounded-md text-sm text-dark-blue focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple transition-colors"
		>
			{OPTIONS.map((opt) => (
				<option key={opt.value} value={opt.value}>
					{opt.label}
				</option>
			))}
		</select>
	</div>
);

const SectionConfig = ({ title, config, onChange }) => (
	<div className="flex flex-col p-4 bg-gray-50 rounded-lg border border-gray-100">
		<h4 className="font-semibold text-dark-blue mb-3">{title}</h4>
		<div className="flex gap-4">
			<PositionSelector
				label="Left"
				value={config.left}
				onChange={(val) => onChange({ ...config, left: val })}
			/>
			<PositionSelector
				label="Right"
				value={config.right}
				onChange={(val) => onChange({ ...config, right: val })}
			/>
		</div>
	</div>
);

const PDFExportOptionsModal = ({
	isOpen,
	onClose,
	onConfirm,
	defaultOptions,
	documentTypeName = '',
	hasWatermarkForExport,
	isEssentialsPlan,
	hasWatermarkRemoval,
}) => {
	const typeDefaults = useMemo(
		() => getDefaultExportConfig(documentTypeName),
		[documentTypeName]
	);

	const [headerConfig, setHeaderConfig] = useState(typeDefaults.header);
	const [footerConfig, setFooterConfig] = useState(typeDefaults.footer);

	const isGraduateDoc = useMemo(() => {
		const n = (documentTypeName || '').toLowerCase();
		return /dissertation|thesis/.test(n);
	}, [documentTypeName]);

	const isProfessionalPaper = useMemo(() => {
		const n = (documentTypeName || '').toLowerCase();
		return (
			n.includes('manuscript') ||
			(n.includes('academic') && n.includes('professional')) ||
			(n.includes('professional') && n.includes('paper'))
		);
	}, [documentTypeName]);

	useEffect(() => {
		if (isOpen) {
			const base = defaultOptions || typeDefaults;
			if (base?.header) {
				setHeaderConfig({
					left: normalizeHeaderSlot(base.header.left),
					right: normalizeHeaderSlot(base.header.right),
				});
			}
			if (base?.footer) {
				setFooterConfig({
					left: normalizeHeaderSlot(base.footer.left),
					right: normalizeHeaderSlot(base.footer.right),
				});
			}
		}
	}, [isOpen, defaultOptions, typeDefaults]);

	const handleConfirm = () => {
		onConfirm({
			header: headerConfig,
			footer: footerConfig,
		});
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/70" onClick={onClose} />

			<div className="relative z-10 w-full max-w-[500px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
				<div className="flex items-center justify-between px-6 py-4 border-b border-lightGrey bg-white">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-full bg-purple/10 flex items-center justify-center text-purple">
							<LayoutTemplate size={20} />
						</div>
						<h2 className="text-xl font-bold text-dark-blue">PDF Export Options</h2>
					</div>
					<button
						onClick={onClose}
						className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
					>
						<X size={20} />
					</button>
				</div>

				<div className="p-6 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
					<p className="text-sm text-gray-600">
						Adjust header and page number settings for your exported document.
					</p>

					<p className="text-xs text-gray-600">
						Student papers use page numbers only. Professional papers include a running head and page
						numbers.
					</p>

					{isGraduateDoc && (
						<p className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-md p-3">
							Thesis and Dissertation documents automatically use Roman numerals for front matter and
							restart Arabic numbering at Chapter 1 based on standard academic formatting rules.
						</p>
					)}

					{isProfessionalPaper && (
						<p className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-md p-3">
							Running heads are not shown in the editor—they appear only in PDF export. Set{' '}
							<strong>Header → Left</strong> to <strong>Running head</strong> when your journal or
							program requires one.
						</p>
					)}

					{typeof hasWatermarkForExport === 'boolean' && (
						<p className="text-xs text-gray-600">
							{hasWatermarkForExport
								? isEssentialsPlan && !hasWatermarkRemoval
									? 'This Essentials export will include a watermark on every page.'
									: 'Your export will follow your selected settings and plan features.'
								: 'Your export will follow your selected settings and plan features.'}
						</p>
					)}

					<SectionConfig title="Header" config={headerConfig} onChange={setHeaderConfig} />

					<SectionConfig title="Footer" config={footerConfig} onChange={setFooterConfig} />
				</div>

				<div className="px-6 py-4 bg-gray-50 border-t border-lightGrey flex justify-end gap-3">
					<Button variant="outline-gradient-text" onClick={onClose} className="px-6">
						Cancel
					</Button>
					<Button onClick={handleConfirm} className="px-8">
						Export PDF
					</Button>
				</div>
			</div>
		</div>
	);
};

export default PDFExportOptionsModal;
